import { ApiError, DEPLOYMENT_KEY_IN_USE_CODE } from "@/lib/api/client";
import type { App, AppCreationRequest } from "../api/apps.api";
import type { Deployment } from "@/features/deployments/api/deployments.api";
import {
  DEPLOYMENT_KEY_MAX_LENGTH,
  DEPLOYMENT_KEY_MIN_LENGTH,
  validateDeploymentKey,
} from "@/features/deployments/lib/deployment-key";

export { DEPLOYMENT_KEY_MAX_LENGTH, DEPLOYMENT_KEY_MIN_LENGTH };
export const NAME_MAX_LENGTH = 255;
const NAME_FORBIDDEN_CHARACTERS = new Set(["\\", "/", "?", ":"]);

export type AppNameIssue = "required" | "tooLong" | "charset";

export type RowIssue =
  | "nameRequired"
  | "nameTooLong"
  | "nameCharset"
  | "duplicateName"
  | "keyLength"
  | "keyCharset"
  | "duplicateKey";

export interface ImportRow {
  id: string;
  name: string;
  key: string;
}

export interface ImportPlan {
  appName: string;
  rows: ImportRow[];
  useExistingApp: boolean;
}

export type AppOutcome =
  | { phase: "pending" }
  | { phase: "creating" }
  | { phase: "ready"; appName: string; existed: boolean }
  | { phase: "failed"; kind: AppFailureKind; message?: string };

export type AppFailureKind =
  "nameTaken" | "forbidden" | "rejected" | "unauthorized" | "limited" | "unreadable" | "unknown";

export type RowFailureKind =
  | "keyInUse"
  | "nameTaken"
  | "keyMismatch"
  | "earlierKeyStored"
  | "keyOnEarlierName"
  | "conflictCleared"
  | "forbidden"
  | "rejected"
  | "unauthorized"
  | "limited"
  | "appMissing"
  | "unknown";

export type RowConfirmation = "create" | "listing";

export type RowOutcome =
  | { phase: "pending" }
  | { phase: "creating" }
  | { phase: "done"; confirmedBy: RowConfirmation; storedKey?: string }
  | {
      phase: "failed";
      kind: RowFailureKind;
      message?: string;
      earlierName?: string;
      storedKey?: string;
    };

type RowFailure = Extract<RowOutcome, { phase: "failed" }>;

const HIDDEN_KEY = "(hidden)";

function readableKey(key: unknown): string | undefined {
  return typeof key === "string" && key && key !== HIDDEN_KEY ? key : undefined;
}

function rowDone(confirmedBy: RowConfirmation, storedKey?: string): RowOutcome {
  return storedKey ? { phase: "done", confirmedBy, storedKey } : { phase: "done", confirmedBy };
}

const HALTING_KINDS: ReadonlySet<RowFailureKind> = new Set([
  "unauthorized",
  "limited",
  "appMissing",
]);

export interface UnconfirmedKey {
  name: string;
  key: string;
}

export interface ImportProgress {
  app: AppOutcome;
  rows: Record<string, RowOutcome>;
  unconfirmedKeys: UnconfirmedKey[];
  priorDeploymentNames: string[];
}

export interface BlankKeyRow {
  name: string;
  storedKey?: string;
}

export interface BlankKeyRows {
  generated: BlankKeyRow[];
  found: BlankKeyRow[];
}

export interface ImportApi {
  createApp: (body: AppCreationRequest) => Promise<{ app: App }>;
  createDeployment: (
    appName: string,
    name: string,
    key?: string,
  ) => Promise<{ deployment: Deployment }>;
  listDeployments: (appName: string) => Promise<{ deployments: Deployment[] }>;
}

export function initialProgress(rows: ImportRow[]): ImportProgress {
  return {
    app: { phase: "pending" },
    rows: Object.fromEntries(rows.map((row) => [row.id, { phase: "pending" } as RowOutcome])),
    unconfirmedKeys: [],
    priorDeploymentNames: [],
  };
}

function hasForbiddenNameCharacter(value: string): boolean {
  for (const character of value) {
    if (NAME_FORBIDDEN_CHARACTERS.has(character)) return true;
    const code = character.codePointAt(0) ?? 0;
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) return true;
  }
  return false;
}

export function validateAppName(name: string): AppNameIssue | null {
  const trimmed = name.trim();
  if (!trimmed) return "required";
  if ([...trimmed].length > NAME_MAX_LENGTH) return "tooLong";
  if (hasForbiddenNameCharacter(trimmed)) return "charset";
  return null;
}

export function validateRows(
  rows: ImportRow[],
  locked: ReadonlySet<string> = new Set(),
): Record<string, RowIssue> {
  const issues: Record<string, RowIssue> = {};
  const takenNames = new Set<string>();
  const takenKeys = new Set<string>();

  for (const row of rows) {
    if (!locked.has(row.id)) continue;
    if (row.name.trim()) takenNames.add(row.name.trim());
    if (row.key.trim()) takenKeys.add(row.key.trim());
  }

  for (const row of rows) {
    if (locked.has(row.id)) continue;

    const name = row.name.trim();
    const key = row.key.trim();

    if (!name) {
      issues[row.id] = "nameRequired";
    } else if ([...name].length > NAME_MAX_LENGTH) {
      issues[row.id] = "nameTooLong";
    } else if (hasForbiddenNameCharacter(name)) {
      issues[row.id] = "nameCharset";
    } else if (takenNames.has(name)) {
      issues[row.id] = "duplicateName";
    }

    if (!issues[row.id] && key) {
      const keyIssue = validateDeploymentKey(key);
      if (keyIssue === "length") {
        issues[row.id] = "keyLength";
      } else if (keyIssue === "charset") {
        issues[row.id] = "keyCharset";
      } else if (takenKeys.has(key)) {
        issues[row.id] = "duplicateKey";
      }
    }

    if (name) takenNames.add(name);
    if (key) takenKeys.add(key);
  }

  return issues;
}

export function isComplete(progress: ImportProgress, rows: ImportRow[]): boolean {
  return (
    progress.app.phase === "ready" && rows.every((row) => progress.rows[row.id]?.phase === "done")
  );
}

export function blankKeyRows(progress: ImportProgress, rows: ImportRow[]): BlankKeyRows {
  const generated: BlankKeyRow[] = [];
  const found: BlankKeyRow[] = [];

  for (const row of rows) {
    if (row.key.trim()) continue;
    const outcome = progress.rows[row.id];
    if (outcome?.phase !== "done") continue;
    const entry: BlankKeyRow = outcome.storedKey
      ? { name: row.name.trim(), storedKey: outcome.storedKey }
      : { name: row.name.trim() };
    (outcome.confirmedBy === "create" ? generated : found).push(entry);
  }

  return { generated, found };
}

export function serverMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError) || typeof error.error !== "string") return undefined;
  const message = error.error.trim();
  if (!message) return undefined;
  if (message.startsWith("[") || message.startsWith("{")) return undefined;
  return message;
}

function statusOf(error: unknown): number | undefined {
  return error instanceof ApiError ? error.status : undefined;
}

function rowFailure(error: unknown): RowFailure {
  const status = statusOf(error);
  if (status === 403) return { phase: "failed", kind: "forbidden", message: serverMessage(error) };
  if (status === 401) return { phase: "failed", kind: "unauthorized" };
  if (status === 429) return { phase: "failed", kind: "limited", message: serverMessage(error) };
  if (status === 404) return { phase: "failed", kind: "appMissing" };
  if (status === 400) return { phase: "failed", kind: "rejected", message: serverMessage(error) };
  return { phase: "failed", kind: "unknown", message: serverMessage(error) };
}

interface AppStep {
  app: AppOutcome;
  priorDeploymentNames: string[];
}

function appCreateFailure(error: unknown): AppOutcome {
  const status = statusOf(error);
  if (status === 403) return { phase: "failed", kind: "forbidden", message: serverMessage(error) };
  if (status === 401) return { phase: "failed", kind: "unauthorized" };
  if (status === 429) return { phase: "failed", kind: "limited", message: serverMessage(error) };
  if (status === 400) return { phase: "failed", kind: "rejected", message: serverMessage(error) };
  return { phase: "failed", kind: "unknown", message: serverMessage(error) };
}

function appAdoptionFailure(error: unknown): AppOutcome {
  const status = statusOf(error);
  if (status === 401) return { phase: "failed", kind: "unauthorized" };
  if (status === 429) return { phase: "failed", kind: "limited", message: serverMessage(error) };
  return { phase: "failed", kind: "unreadable" };
}

async function adoptTheApp(appName: string, api: ImportApi): Promise<AppStep> {
  try {
    const { deployments } = await api.listDeployments(appName);
    const priorDeploymentNames = (deployments ?? []).map((deployment) => deployment.name);
    return { app: { phase: "ready", appName, existed: true }, priorDeploymentNames };
  } catch (error) {
    return { app: appAdoptionFailure(error), priorDeploymentNames: [] };
  }
}

async function createTheApp(plan: ImportPlan, api: ImportApi): Promise<AppStep> {
  const appName = plan.appName.trim();
  try {
    await api.createApp({ name: appName, manuallyProvisionDeployments: true });
    return { app: { phase: "ready", appName, existed: false }, priorDeploymentNames: [] };
  } catch (error) {
    if (statusOf(error) !== 409) return { app: appCreateFailure(error), priorDeploymentNames: [] };
    if (!plan.useExistingApp) {
      return { app: { phase: "failed", kind: "nameTaken" }, priorDeploymentNames: [] };
    }
  }
  return adoptTheApp(appName, api);
}

function reconcileConflict(
  row: ImportRow,
  existing: Deployment[] | null,
  heldBeforeImport: boolean,
  sentKeys: string[],
): RowOutcome {
  const name = row.name.trim();
  const key = row.key.trim();

  if (!existing) return { phase: "failed", kind: "unknown" };

  const match = existing.find((deployment) => deployment.name === name);
  if (!match) return { phase: "failed", kind: key ? "keyInUse" : "conflictCleared" };
  if (key && match.key === key) return rowDone("listing");
  if (heldBeforeImport) return { phase: "failed", kind: "nameTaken" };
  const stored = match.key;
  if (stored !== undefined && sentKeys.includes(stored)) {
    return { phase: "failed", kind: "earlierKeyStored", storedKey: stored };
  }
  if (key) return { phase: "failed", kind: "keyMismatch" };
  return rowDone("listing", readableKey(stored));
}

export async function runImport(
  plan: ImportPlan,
  progress: ImportProgress,
  api: ImportApi,
  onProgress?: (progress: ImportProgress) => void,
): Promise<ImportProgress> {
  let current: ImportProgress = {
    app: progress.app,
    rows: { ...progress.rows },
    unconfirmedKeys: [...progress.unconfirmedKeys],
    priorDeploymentNames: [...progress.priorDeploymentNames],
  };
  const publish = (next: ImportProgress) => {
    current = next;
    onProgress?.(current);
    return current;
  };

  if (current.app.phase !== "ready") {
    publish({ ...current, app: { phase: "creating" } });
    const appStep = await createTheApp(plan, api);
    publish({ ...current, ...appStep });
  }

  const app = current.app;
  if (app.phase !== "ready") return current;

  const resolveConflict = async (row: ImportRow): Promise<RowOutcome> => {
    const name = row.name.trim();
    try {
      const existing = (await api.listDeployments(app.appName)).deployments ?? null;
      return reconcileConflict(
        row,
        existing,
        current.priorDeploymentNames.includes(name),
        current.unconfirmedKeys.filter((sent) => sent.name === name).map((sent) => sent.key),
      );
    } catch (error) {
      return rowFailure(error);
    }
  };

  const resolveKeyInUse = async (name: string, key: string): Promise<RowOutcome> => {
    if (!key) return { phase: "failed", kind: "unknown" };
    const keyInUse: RowFailure = { phase: "failed", kind: "keyInUse" };
    const earlierNames = current.unconfirmedKeys
      .filter((sent) => sent.key === key && sent.name !== name)
      .map((sent) => sent.name);
    if (earlierNames.length === 0) return keyInUse;
    try {
      const existing = (await api.listDeployments(app.appName)).deployments ?? [];
      const holder = existing.find(
        (deployment) => deployment.key === key && earlierNames.includes(deployment.name),
      );
      return holder
        ? { phase: "failed", kind: "keyOnEarlierName", earlierName: holder.name }
        : keyInUse;
    } catch (error) {
      const failure = rowFailure(error);
      return HALTING_KINDS.has(failure.kind) ? failure : keyInUse;
    }
  };

  for (const row of plan.rows) {
    if (current.rows[row.id]?.phase === "done") continue;

    const name = row.name.trim();
    const key = row.key.trim();

    publish({ ...current, rows: { ...current.rows, [row.id]: { phase: "creating" } } });

    let outcome: RowOutcome;
    let keyLeftUnconfirmed = false;
    try {
      const created = await api.createDeployment(app.appName, name, key || undefined);
      outcome = rowDone("create", key ? undefined : readableKey(created?.deployment?.key));
    } catch (error) {
      if (statusOf(error) !== 409) {
        const failure = rowFailure(error);
        keyLeftUnconfirmed = Boolean(key) && failure.kind === "unknown";
        outcome = failure;
      } else if (error instanceof ApiError && error.code === DEPLOYMENT_KEY_IN_USE_CODE) {
        outcome = await resolveKeyInUse(name, key);
      } else {
        outcome = await resolveConflict(row);
      }
    }

    publish({
      ...current,
      rows: { ...current.rows, [row.id]: outcome },
      unconfirmedKeys: keyLeftUnconfirmed
        ? [...current.unconfirmedKeys, { name, key }]
        : current.unconfirmedKeys,
    });

    if (outcome.phase === "failed" && HALTING_KINDS.has(outcome.kind)) break;
  }

  return current;
}
